const config = window.BospStorefrontTrackingConfig;

if (config) {
  window.dataLayer = window.dataLayer || [];

  const trackingContext = {
    store_id: config.store_id,
    market: config.market,
    gtm_context: 'theme',
  };

  /** @param {string} eventName */
  function eventId(eventName) {
    const randomId = globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    return `theme-${eventName}-${randomId}`;
  }

  /** @param {{ name: string, shopify_event_name?: string, search_term?: string, ecommerce?: object, placement?: string, cart_value?: number }} definition */
  function pushEvent(definition) {
    if (!trackingActive) return;

    window.dataLayer.push({ ecommerce: null, search_term: null });
    window.dataLayer.push({
      event: definition.name,
      shopify_event_name: definition.shopify_event_name || null,
      event_id: eventId(definition.name),
      event_timestamp: new Date().toISOString(),
      page_location: window.location.href,
      page_referrer: document.referrer || null,
      page_title: document.title || null,
      ...trackingContext,
      ...(definition.search_term ? { search_term: definition.search_term } : {}),
      ...(definition.ecommerce ? { ecommerce: definition.ecommerce } : {}),
      ...(definition.placement ? { placement: definition.placement } : {}),
      ...(definition.cart_value != null ? { cart_value: definition.cart_value } : {}),
    });
  }

  /** @param {{ name: string, ecommerce?: any }} definition */
  function hydrateVisibleCollectionItems(definition) {
    if (definition.name !== 'view_item_list') return definition;

    const cards = document.querySelectorAll('#MainContent results-list product-card[data-tracking-item]');
    const listId = definition.ecommerce?.item_list_id || null;
    const listName = definition.ecommerce?.item_list_name || null;
    const items = [];

    for (const [index, card] of Array.from(cards).entries()) {
      try {
        const item = JSON.parse(card.getAttribute('data-tracking-item') || '');
        items.push({ ...item, index: index + 1, item_list_id: listId, item_list_name: listName });
      } catch {
        // Ignore placeholder or malformed cards without blocking the list-view event.
      }
    }

    definition.ecommerce.items = items;
    return definition;
  }

  /** @param {Record<string, any>} item */
  function itemKey(item) {
    return String(item.shopify_variant_id || item.variant_id || item.id || item.item_id || '');
  }

  /** @param {Record<string, any>} item */
  function rememberItemCategory(item) {
    const category = String(item.item_category || '').trim();
    if (!category) return;

    const variantId = String(item.shopify_variant_id || item.variant_id || item.id || item.item_id || '');
    const productId = String(item.shopify_product_id || item.product_id || '');

    if (variantId) knownCategories.set(`variant:${variantId}`, category);
    if (productId) knownCategories.set(`product:${productId}`, category);
  }

  /** @param {Record<string, any>} item */
  function knownItemCategory(item) {
    const variantId = String(item.shopify_variant_id || item.variant_id || item.id || item.item_id || '');
    const productId = String(item.shopify_product_id || item.product_id || '');

    return knownCategories.get(`variant:${variantId}`) || knownCategories.get(`product:${productId}`) || '';
  }

  function rememberRenderedItemCategories() {
    const renderedItems = [
      ...(config.initial_cart?.items || []),
      ...(config.events || []).flatMap((definition) => definition.ecommerce?.items || []),
    ];

    for (const card of document.querySelectorAll('[data-tracking-item]')) {
      try {
        renderedItems.push(JSON.parse(card.getAttribute('data-tracking-item') || ''));
      } catch {
        // Ignore malformed tracking data; it must never interrupt cart behavior.
      }
    }

    renderedItems.forEach(rememberItemCategory);
    return renderedItems;
  }

  /**
   * Sends category metadata only to Shopify custom pixels. It deliberately does
   * not use cart attributes or line-item properties, so it cannot appear on an order.
   * @param {Array<Record<string, any>>} items
   */
  function publishCartCategories(items) {
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;

    const categoryItems = items
      .map((item) => ({
        product_id: String(item.shopify_product_id || item.product_id || ''),
        variant_id: String(item.shopify_variant_id || item.variant_id || item.id || item.item_id || ''),
        item_category: String(item.item_category || knownItemCategory(item) || ''),
      }))
      .filter((item) => item.item_category && (item.variant_id || item.product_id));

    window.Shopify.analytics.publish('bosp:cart_categories', { items: categoryItems });
  }

  /** @param {Record<string, any>} item */
  function normalizeAjaxCartItem(item) {
    const priceInMinorUnits = item.final_price ?? item.price ?? 0;
    const normalizedItem = {
      item_id: String(item.variant_id || item.id || ''),
      item_name: item.product_title || item.title || '',
      item_brand: item.vendor || '',
      item_category: item.product_type || knownItemCategory(item),
      item_variant: item.variant_title || '',
      price: Number(priceInMinorUnits) / 100,
      quantity: Number(item.quantity) || 0,
      sku: item.sku || '',
      shopify_product_id: String(item.product_id || ''),
      shopify_variant_id: String(item.variant_id || item.id || ''),
      item_url: item.url || '',
      index: null,
      item_list_id: null,
      item_list_name: null,
    };

    rememberItemCategory(normalizedItem);
    return normalizedItem;
  }

  /** @param {Array<Record<string, any>>} items */
  function itemMap(items) {
    return new Map(items.map((item) => [itemKey(item), item]));
  }

  let previousItems = itemMap(config.initial_cart?.items || []);
  const knownCategories = new Map();
  let trackingActive = false;
  let listenersInitialized = false;

  function handleCartUpdate(event) {
    if (event.detail?.data?.didError) return;

    const cart = event.detail?.resource;
    if (!Array.isArray(cart?.items)) return;

    rememberRenderedItemCategories();
    const nextItemsArray = cart.items.map(normalizeAjaxCartItem);
    const nextItems = itemMap(nextItemsArray);
    const currency = cart.currency || config.currency;
    const itemKeys = new Set([...previousItems.keys(), ...nextItems.keys()]);

    for (const key of itemKeys) {
      if (!key) continue;

      const previousItem = previousItems.get(key);
      const nextItem = nextItems.get(key);
      const previousQuantity = Number(previousItem?.quantity) || 0;
      const nextQuantity = Number(nextItem?.quantity) || 0;
      const quantityDifference = nextQuantity - previousQuantity;

      if (quantityDifference === 0) continue;

      const isAddition = quantityDifference > 0;
      const sourceItem = isAddition ? nextItem : previousItem;
      const item = { ...sourceItem, quantity: Math.abs(quantityDifference) };

      pushEvent({
        name: isAddition ? 'add_to_cart' : 'remove_from_cart',
        shopify_event_name: isAddition ? 'product_added_to_cart' : 'product_removed_from_cart',
        ecommerce: {
          currency,
          value: Number(item.price) * item.quantity,
          items: [item],
        },
      });
    }

    previousItems = nextItems;
    publishCartCategories(nextItemsArray);
  }

  function handleProductSelection(event) {
    const productCard = event.target.closest(
      '#MainContent results-list product-card[data-tracking-item], #MainContent .resource-list product-card[data-tracking-item]'
    );
    if (!productCard || !event.target.closest('a[href]')) return;

    try {
      const item = JSON.parse(productCard.getAttribute('data-tracking-item') || '');
      const cards = Array.from(
        document.querySelectorAll(
          '#MainContent results-list product-card[data-tracking-item], #MainContent .resource-list product-card[data-tracking-item]'
        )
      );
      const listDefinition = (config.events || []).find((definition) => definition.name === 'view_item_list');

      pushEvent({
        name: 'select_item',
        shopify_event_name: 'product_selected',
        ecommerce: {
          item_list_id: listDefinition?.ecommerce?.item_list_id || null,
          item_list_name: listDefinition?.ecommerce?.item_list_name || null,
          items: [
            {
              ...item,
              index: cards.indexOf(productCard) + 1,
              item_list_id: listDefinition?.ecommerce?.item_list_id || null,
              item_list_name: listDefinition?.ecommerce?.item_list_name || null,
            },
          ],
        },
      });
    } catch {
      // Ignore malformed card data without interrupting navigation.
    }
  }

  /** @param {MouseEvent} event */
  function handleB2bInfoClick(event) {
    const link = event.target.closest('[data-b2b-info-link]');
    if (!link) return;

    const cartValueRaw = link.getAttribute('data-cart-value');
    const cartValue = cartValueRaw == null || cartValueRaw === '' ? 0 : Number(cartValueRaw);

    pushEvent({
      name: 'b2b_info_click',
      placement: link.getAttribute('data-placement') || undefined,
      cart_value: Number.isFinite(cartValue) ? cartValue : 0,
    });
  }

  function activateStorefrontTracking() {
    if (trackingActive) return;
    trackingActive = true;
    const renderedItems = rememberRenderedItemCategories();
    publishCartCategories(renderedItems);

    for (const definition of config.events || []) {
      pushEvent(hydrateVisibleCollectionItems(definition));
    }

    if (!listenersInitialized) {
      listenersInitialized = true;
      document.addEventListener('cart:update', handleCartUpdate);
      document.addEventListener('click', handleProductSelection);
      document.addEventListener('click', handleB2bInfoClick);
    }
  }

  activateStorefrontTracking();
}
